import OpenAI from 'openai';

/**
 * Initializes the OpenAI client with the API key from environment variables.
 * @returns {OpenAI} Configured OpenAI client instance.
 */
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true, // Required for client-side usage in React
});

/**
 * Generates a chat completion response based on user input using GPT-5.
 * @param {string} userMessage - The user's input message.
 * @param {string} reasoningEffort - Reasoning level: 'minimal', 'low', 'medium', 'high'.
 * @param {string} verbosity - Response detail level: 'low', 'medium', 'high'.
 * @returns {Promise<string>} The assistant's response.
 */
export async function getGPT5ChatCompletion(userMessage, reasoningEffort = 'medium', verbosity = 'medium') {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o', // Using available model instead of gpt-5
      messages: [
        { role: 'system', content: 'You are a helpful assistant specialized in dinner party planning, recipe suggestions, and culinary advice.' },
        { role: 'user', content: userMessage },
      ],
      max_tokens: 2000,
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    console.error('Error in GPT chat completion:', error);
    throw error;
  }
}

/**
 * Generates recipe suggestions based on ingredients and preferences.
 * @param {Array} ingredients - List of available ingredients.
 * @param {Object} preferences - User preferences (dietary restrictions, cuisine, etc.).
 * @returns {Promise<Object>} Structured recipe suggestions.
 */
export async function generateRecipeSuggestions(ingredients, preferences = {}) {
  try {
    const prompt = `Based on these ingredients: ${ingredients?.join(', ')} and preferences: ${JSON.stringify(preferences)}, suggest 3 recipes with detailed instructions, prep time, and serving sizes. Format as JSON with recipe objects containing: name, ingredients, instructions, prepTime, servings, cuisine, dietaryRestrictions.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are a professional chef and recipe developer. Provide practical, delicious recipes that can be made with the given ingredients.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'recipe_suggestions',
          schema: {
            type: 'object',
            properties: {
              recipes: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    ingredients: { type: 'array', items: { type: 'string' } },
                    instructions: { type: 'string' },
                    prepTime: { type: 'number' },
                    servings: { type: 'number' },
                    cuisine: { type: 'string' },
                    dietaryRestrictions: { type: 'array', items: { type: 'string' } }
                  },
                  required: ['name', 'ingredients', 'instructions', 'prepTime', 'servings'],
                  additionalProperties: false
                }
              }
            },
            required: ['recipes'],
            additionalProperties: false
          }
        }
      }
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error generating recipe suggestions:', error);
    throw error;
  }
}

/**
 * Generates dinner party menu suggestions based on guest preferences and constraints.
 * @param {Object} partyDetails - Details about the dinner party (guest count, preferences, theme, etc.).
 * @returns {Promise<Object>} Structured menu suggestions with courses.
 */
export async function generateMenuSuggestions(partyDetails) {
  try {
    const { guestCount, dietaryRestrictions, theme, budget, duration } = partyDetails;
    
    const prompt = `Create a dinner party menu for ${guestCount} guests with these details:
    - Dietary restrictions: ${dietaryRestrictions?.join(', ') || 'None'}
    - Theme: ${theme || 'General'}
    - Budget: ${budget || 'Moderate'}
    - Duration: ${duration || '2-3 hours'}
    
    Provide a complete menu with appetizers, main course, sides, and dessert. Include prep times and cooking tips.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert dinner party planner and chef. Create cohesive, impressive menus that consider timing, dietary needs, and presentation.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'menu_suggestions',
          schema: {
            type: 'object',
            properties: {
              menu: {
                type: 'object',
                properties: {
                  appetizers: { type: 'array', items: { type: 'string' } },
                  mainCourse: { type: 'string' },
                  sides: { type: 'array', items: { type: 'string' } },
                  dessert: { type: 'string' },
                  wines: { type: 'array', items: { type: 'string' } }
                },
                required: ['appetizers', 'mainCourse', 'sides', 'dessert'],
                additionalProperties: false
              },
              timeline: {
                type: 'object',
                properties: {
                  dayBefore: { type: 'array', items: { type: 'string' } },
                  dayOf: { type: 'array', items: { type: 'string' } },
                  lastHour: { type: 'array', items: { type: 'string' } }
                },
                required: ['dayBefore', 'dayOf', 'lastHour'],
                additionalProperties: false
              },
              tips: { type: 'array', items: { type: 'string' } }
            },
            required: ['menu', 'timeline', 'tips'],
            additionalProperties: false
          }
        }
      }
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error generating menu suggestions:', error);
    throw error;
  }
}

/**
 * Generates shopping list based on selected dishes and party size.
 * @param {Array} dishes - Array of selected dishes.
 * @param {number} guestCount - Number of guests.
 * @returns {Promise<Object>} Organized shopping list by category.
 */
export async function generateShoppingList(dishes, guestCount) {
  try {
    const dishNames = dishes?.map(dish => dish?.name)?.join(', ');
    const prompt = `Create a comprehensive shopping list for preparing these dishes: ${dishNames} for ${guestCount} guests. Organize by grocery store categories and include estimated quantities.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are a meal planning expert. Create detailed, organized shopping lists that account for proper quantities and avoid waste.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'shopping_list',
          schema: {
            type: 'object',
            properties: {
              categories: {
                type: 'object',
                properties: {
                  produce: { type: 'array', items: { type: 'string' } },
                  meat: { type: 'array', items: { type: 'string' } },
                  dairy: { type: 'array', items: { type: 'string' } },
                  pantry: { type: 'array', items: { type: 'string' } },
                  beverages: { type: 'array', items: { type: 'string' } },
                  other: { type: 'array', items: { type: 'string' } }
                },
                additionalProperties: false
              },
              estimatedCost: { type: 'string' },
              notes: { type: 'array', items: { type: 'string' } }
            },
            required: ['categories'],
            additionalProperties: false
          }
        }
      }
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error generating shopping list:', error);
    throw error;
  }
}

/**
 * Streams a chat completion response chunk by chunk for real-time interaction.
 * @param {string} userMessage - The user's input message.
 * @param {Function} onChunk - Callback to handle each streamed chunk.
 */
export async function getStreamingChatCompletion(userMessage, onChunk) {
  try {
    const stream = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { role: 'system', content: 'You are a helpful assistant specialized in dinner party planning and culinary advice.' },
        { role: 'user', content: userMessage },
      ],
      stream: true,
    });

    for await (const chunk of stream) {
      const content = chunk?.choices?.[0]?.delta?.content || '';
      if (content) {
        onChunk(content);
      }
    }
  } catch (error) {
    console.error('Error in streaming chat completion:', error);
    throw error;
  }
}

/**
 * Analyzes an image using OpenAI's vision capabilities (for recipe analysis, food photos, etc.).
 * @param {string} imageUrl - URL of the image to analyze.
 * @param {string} analysisType - Type of analysis: 'recipe', 'ingredient', 'presentation'.
 * @returns {Promise<Object>} Detailed image analysis.
 */
export async function analyzeImage(imageUrl, analysisType = 'recipe') {
  try {
    const systemPrompts = {
      recipe: 'Analyze this food image and provide recipe suggestions, cooking techniques, and ingredient identification.',
      ingredient: 'Identify all ingredients visible in this image and suggest recipes that could be made with them.',
      presentation: 'Analyze the food presentation, plating techniques, and suggest improvements for dinner party presentation.'
    };

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: systemPrompts?.[analysisType] || systemPrompts?.recipe
        },
        {
          role: 'user',
          content: [
            { type: 'text', text: `Please analyze this image with focus on: ${analysisType}` },
            { type: 'image_url', image_url: { url: imageUrl } },
          ],
        },
      ],
    });

    return {
      analysis: response?.choices?.[0]?.message?.content,
      analysisType,
      imageUrl,
    };
  } catch (error) {
    console.error('Error analyzing image:', error);
    throw error;
  }
}

/**
 * Checks if text content violates content policies (for user-generated content).
 * @param {string} text - The text to moderate.
 * @returns {Promise<object>} Moderation results.
 */
export async function moderateText(text) {
  try {
    const response = await openai?.moderations?.create({
      model: 'text-moderation-latest',
      input: text,
    });

    return response?.results?.[0];
  } catch (error) {
    console.error('Error moderating text:', error);
    throw error;
  }
}

export default openai;